import { useNavigate, useLocation } from 'react-router-dom';
import { useRef, useState, useEffect } from 'react';
import styles from './ViewScenarioMobile.module.css';
import { api } from '../../api';
import { useAuth } from '../../context/AuthContext';
import { ScenarioCharacterCard } from './ScenarioCharacterCard';
import { ScenarioPersonaCard, ScenarioDescription } from './ScenarioCards';
import { ScenarioDefinitionType } from '../../api/scenarioDefinitions';
import { ScenarioCharacter, ScenarioPersona, CreateScenarioPayload } from '../../api/types/scenario';
import { PersonaListResponse, CharacterListResponse } from '../../api/types';
import ScenarioPersonaSelectionPopUp from './ScenarioPersonaSelectionPopUp';
import ScenarioCharacterSelectionPopUp from './ScenarioCharacterSelectionPopUp';
import { clipboardIcon, shareIcon, visibilityPublicIcon, visibilityPrivateIcon, visibilityUnlistedIcon, charactersIcon } from '../../assets/icons';
import { fallbackImage } from '../../assets/images';
import CreatedBy from './CreatedBy';
import { ScenarioStartButton, ScenarioEditButton } from './ScenarioMajorButton';
import LoginModal from '../shared/LoginModal';
import { AdventureCharacterCard } from './ScenarioCharacterCard';

// MatureTag component to display an 'M' with a circular white background
const MatureTag = () => {
  return (
    <div className={styles.matureTagContainer}>
      <div className={styles.matureTag}>
        <span className={styles.matureTagText}>M</span>
      </div>
      <div className={styles.matureTooltip}>
        This scenario is for mature audiences
      </div>
    </div>
  );
};

interface ViewScenarioMobileProps {
  definition: ScenarioDefinitionType;
  isEditable: boolean;
  onRefresh?: () => Promise<void>;
}

function ViewScenarioMobile({ definition, isEditable, onRefresh }: ViewScenarioMobileProps): JSX.Element {
  const navigate = useNavigate();
  const imageRef = useRef<HTMLImageElement>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const { user } = useAuth();
  const [showPersonaModal, setShowPersonaModal] = useState(false);
  const [showCharacterModal, setShowCharacterModal] = useState(false);
  const [selectedPersona, setSelectedPersona] = useState<ScenarioPersona | null>(null);
  const [selectedCharacter, setSelectedCharacter] = useState<ScenarioCharacter | null>(null);
  const [showNotification, setShowNotification] = useState(false);
  const [timestamp, setTimestamp] = useState<number>(Date.now());
  const [showLoginModal, setShowLoginModal] = useState(false);
  const location = useLocation();
  const [featuredCharacters, setFeaturedCharacters] = useState<CharacterListResponse[]>([]);
  const [isLoadingFeaturedCharacters, setIsLoadingFeaturedCharacters] = useState(false);
  
  // Store the scenario definition in state so we can update it
  const [scenarioDef, setScenarioDef] = useState<ScenarioDefinitionType>(definition);
  
  // Update the internal state when the definition prop changes
  useEffect(() => {
    setScenarioDef(definition);
    // Update timestamp to force image refresh
    setTimestamp(Date.now());
  }, [definition]);

  const shouldAutostart = new URLSearchParams(location.search).get('autostart') === 'true';

  // Load featured characters when component mounts
  useEffect(() => {
    const loadFeaturedCharacters = async () => {
      // Only load if we have a character slot and no character is selected yet
      if (scenarioDef.has_character_slot && !selectedCharacter) {
        setIsLoadingFeaturedCharacters(true);
        try {
          // Use trending instead of featured since it's more reliable
          const trending = await api.characters.filter("trending");
          setFeaturedCharacters(trending);
          
          // Randomly select a character from trending list if we have any
          if (trending.length > 0) {
            // Use a deterministic seed based on the scenario ID to ensure
            // the same random character is selected for the same scenario
            const seed = scenarioDef.id.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0);
            const randomIndex = seed % trending.length;
            const randomCharacter = trending[randomIndex];
            
            // Convert to ScenarioCharacter format
            const scenarioCharacter: ScenarioCharacter = {
              id: randomCharacter.id,
              display_name: randomCharacter.display_name,
              tagline: randomCharacter.tagline || "",
              icon: randomCharacter.icon,
              username: randomCharacter.username || randomCharacter.display_name.toLowerCase().replace(/\s+/g, '_'),
              is_mature: false
            };
            
            setSelectedCharacter(scenarioCharacter);
          }
        } catch (error) {
          console.error('Failed to load trending characters:', error);
        } finally {
          setIsLoadingFeaturedCharacters(false);
        }
      }
    };
    
    loadFeaturedCharacters();
  }, [scenarioDef.id, scenarioDef.has_character_slot, selectedCharacter]);

  useEffect(() => {
    // Set the default persona as the initially selected persona
    if (user?.default_persona) {
      setSelectedPersona(user.default_persona as ScenarioPersona);
    }
  }, [user]);

  useEffect(() => {
    if (shouldAutostart && user && selectedPersona) {
      handleStartSession();
    }
  }, [shouldAutostart, user, selectedPersona]);

  const handlePersonaClick = () => {
    setShowPersonaModal(true);
  };
  
  const handleCharacterClick = () => {
    setShowCharacterModal(true);
  };

  const handleSelectPersona = (persona: PersonaListResponse) => {
    setSelectedPersona(persona as unknown as ScenarioPersona);
    setShowPersonaModal(false);
  };
  
  const handleSelectCharacter = (character: ScenarioCharacter) => {
    setSelectedCharacter(character);
    setShowCharacterModal(false);
  };

  const handleStartSession = async (): Promise<void> => {
    try {
      // Check if user is logged in
      if (!user) {
        // Show login modal
        setShowLoginModal(true);
        return;
      }
      
      const personaId = selectedPersona?.id || user?.default_persona?.id;
      
      if (!personaId) {
        console.error('No persona available');
        return;
      }
      
      // Create the payload
      const payload: CreateScenarioPayload = {
        definition: scenarioDef.id,
        persona: personaId
      };
      
      // Include the character ID if has_character_slot is true and a character is selected
      if (scenarioDef.has_character_slot && selectedCharacter) {
        payload.character = selectedCharacter.id;
      }
      
      // Create a new session using the scenarios API
      const session = await api.scenarios.create(payload);
      
      // Navigate to the session page
      navigate(`/sessions/${session.id}`);
    } catch (error) {
      console.error('Failed to create session:', error);
    }
  };

  const handleEditClick = () => {
    navigate(`/scenarios/${scenarioDef.id}?edit=true`);
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
    setImageError(false);
  };

  const handleImageError = () => {
    setImageError(true);
    setImageLoaded(true); // Consider the image "loaded" even if it's the fallback
  };

  const handleShareClick = () => {
    navigator.clipboard.writeText(window.location.href);
    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 2000);
  };

  // Check if the current user is the owner of the scenario
  const isOwner = user?.username === scenarioDef.author;

  // Get visibility icon and label based on the visibility value
  const getVisibilityInfo = () => {
    switch (scenarioDef.visibility) {
      case 'public':
        return {
          icon: visibilityPublicIcon,
          label: 'Public'
        };
      case 'unlisted':
        return {
          icon: visibilityUnlistedIcon,
          label: 'Unlisted'
        };
      case 'private':
        return {
          icon: visibilityPrivateIcon,
          label: 'Private'
        };
      default:
        return {
          icon: visibilityPublicIcon,
          label: 'Public'
        };
    }
  };

  const visibilityInfo = getVisibilityInfo();

  // Update imageSource to include cache-busting parameter
  const imageSource = scenarioDef.icon && !imageError 
    ? `${scenarioDef.icon}?t=${timestamp}` 
    : fallbackImage;

  return (
    <div className={styles.verticalContainer}>
      {/* Author info at top left */}
      <div className={styles.authorInfoTopLeft}>
        <CreatedBy author={scenarioDef.author} />
      </div>

      {/* Share button at top right */}
      <button className={styles.shareButton} onClick={handleShareClick}>
        <img src={shareIcon} alt="Share" className={styles.shareIcon} />
      </button>

      {/* Notification for copy to clipboard */}
      {showNotification && (
        <div className={styles.notification}>
          <img src={clipboardIcon} alt="Copied" className={styles.clipboardIcon} />
          Link copied to clipboard
        </div>
      )}

      <div className={styles.scenarioImageSection}>
        <div className={styles.imageContainer}>
          <div className={styles.imageWrapper}>
            <img 
              ref={imageRef}
              src={imageSource} 
              alt={scenarioDef.display_name} 
              className={styles.fullImage}
              onLoad={handleImageLoad}
              onError={handleImageError}
            />
            <div className={styles.buttonOverlay}>
              <div className={styles.buttonGroup}>
                <ScenarioStartButton onClick={handleStartSession} />
                {isEditable && (
                  <ScenarioEditButton onClick={handleEditClick} />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className={styles.scenarioDetailsSection}>
        <div className={styles.scenarioHeader}>
          <div className={styles.titleContainer}>
            <h1 className={styles.scenarioTitle}>
              {scenarioDef.display_name}
            </h1>
            <div className={styles.titleIndicators}>
              {isOwner && (
                <div 
                  className={styles.visibilityIndicator} 
                  data-tooltip={`Visibility: ${visibilityInfo.label}`}
                >
                  <img src={visibilityInfo.icon} alt={visibilityInfo.label} className={styles.visibilityIcon} />
                </div>
              )}
              {scenarioDef.is_mature && <MatureTag />}
            </div>
          </div>
          
          <ScenarioDescription description={scenarioDef.description} />
          
          <div className={styles.sectionsContainer}>
            {/* Persona Section */}
            <div className={styles.personaSection}>
              <div className={styles.sectionHeader}>
                <h3 className={styles.sectionTitle}>YOU ARE</h3>
              </div>
              <div onClick={handlePersonaClick}>
                <ScenarioPersonaCard persona={selectedPersona || (user?.default_persona as ScenarioPersona)} showSwapIcon={true} />
              </div>
            </div>
            
            {/* Character Selection Section - only shown when has_character_slot is true */}
            {scenarioDef.has_character_slot && (
              <div className={styles.characterSelectionSection}>
                <div className={styles.sectionHeader}>
                  <h3 className={styles.sectionTitle}>YOUR COMPANION</h3>
                </div>
                <div onClick={handleCharacterClick}>
                  <AdventureCharacterCard 
                    character={selectedCharacter || undefined} 
                    showSwapIcon={true}
                    isLoading={isLoadingFeaturedCharacters} 
                  />
                </div>
              </div>
            )}
            
            {/* Characters Section */}
            <div className={styles.charactersSection}>
              <div className={styles.sectionHeader}>
                <h3 className={styles.sectionTitle}>CHARACTERS</h3>
              </div>
              
              <div className={styles.charactersGrid}>
                {scenarioDef.characters && scenarioDef.characters.length > 0 ? (
                  scenarioDef.characters.map((character) => (
                    <ScenarioCharacterCard key={character.id} character={character as ScenarioCharacter} />
                  ))
                ) : (
                  <div className={styles.emptyState}>
                    <p>No characters have been added to this scenario yet.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Persona Selection Modal */}
      <ScenarioPersonaSelectionPopUp
        isOpen={showPersonaModal}
        onClose={() => setShowPersonaModal(false)}
        onSelectPersona={handleSelectPersona}
        selectedPersona={selectedPersona}
        defaultPersonaId={user?.default_persona?.id}
      />
      
      {/* Character Selection Modal */}
      <ScenarioCharacterSelectionPopUp
        isOpen={showCharacterModal}
        onClose={() => setShowCharacterModal(false)}
        onSelectCharacter={handleSelectCharacter}
        selectedCharacter={selectedCharacter}
        scenarioId={scenarioDef.id}
      />

      {showLoginModal && (
        <LoginModal 
          redirectParams={{
            redirectType: 'scenario',
            scenarioDefinitionId: scenarioDef.id
          }}
          onClose={() => setShowLoginModal(false)}
        />
      )}
    </div>
  );
}

export default ViewScenarioMobile; 